<template>
  <vx-card
    v-if="classAssignments"
    class="w-full mb-5"
    :title="$t('class-overview.class-assignment')" title-color="primary"
  >
    <!--CLASS ASSIGNMENT-->
    <li v-if="!showEditForm" class="flex flex-inline justify-between w-full">
      <div class="flex-1">
        <vs-chip class="w-3/5" color="primary" transparent>
          <span>{{ $t('class-overview.assignment-topic') }}</span>
        </vs-chip>
      </div>
      <div class="w-2/5">
        <vs-chip color="primary" transparent>
          {{ $t('class-overview.score') }}
        </vs-chip>
        <vs-chip color="primary" transparent>
          {{ $t('class-overview.due-date') }}
        </vs-chip>
      </div>
    </li>
    <li
      v-for="(assignment, index) in classAssignments" :key="index"
      class="w-full px-3 pl-0" style="list-style: none;">
      <vs-collapse v-if="!assignment.showEditForm" class="m-0 p-0">
        <vs-collapse-item class="m-0 p-0">
          <!--HEADER-->
          <div slot="header" class="m-0 p-0">
            <div class="flex justify-between items-center w-full pr-3">
              <div class="flex-1 flex justify-start items-start">
                <vs-avatar :text="`${index+1}.`" class="bg-primary w-5 h-5 text-sm mr-2 p-2"/>
                <span class="text-sm mt-1"> {{ assignment.topicLocal }} </span>
              </div>
              <div class="flex w-2/5 justify-between mr-3">
                <span class="w-16 text-right text-sm"> {{ assignment.score }} </span>
                <span class="w-16 text-right text-sm"> {{ assignmentDueDateFormatter(assignment.dueDate) }} </span>
              </div>
            </div>
          </div>

          <!--STUDENT ASSIGNMENT -->
          <div class="flex flex-inline justify-between mb-3">
            <h5 color="primary">{{ $t('class-overview.student-assignment-status') }}</h5>
            <vs-chip v-if="assignment.uploadPathUri"
                     class="cursor-pointer w-auto pl-1 pr-4 mr-2 h-4"
                     color="primary" @click.native="onDownloadClick(assignment.uploadPathUri)">
              <vs-avatar
                class="h-5 w-5" color="primary" icon="icon-download" icon-pack="feather"/>
              <span class="text-sm">{{ $t('class-overview.assignment-file') }}</span>
            </vs-chip>
          </div>
          <!--HEADER-->
          <!--BUTTON-->
          <div class="flex flex-inline justify-end mt-2">
            <vs-chip
              class="cursor-pointer w-auto pl-1 pr-4 h-4"
              color="primary" @click.native="onSendAssignmentClick(assignment)">
              <vs-avatar
                class="h-5 w-5" color="primary" icon="icon-send" icon-pack="feather"/>
              <span class="text-sm">{{ $t('class-overview.submit-assignment') }}</span>
            </vs-chip>
          </div>
        </vs-collapse-item>
      </vs-collapse>

      <div v-if="assignment.showEditForm" class="w-full mt-2">
        <student-assignment-form :assignment="assignment"
                                 :classSection="classSection"
                                 :studentEnrollDetailId="studentEnrollDetailId"
                                 :saveStudentAssignment="saveStudentAssignment"
                                 :student="student"
                                 :studentAssignmentProcessing="studentAssignmentProcessing"
                                 :uploadStudentAssignment="uploadStudentAssignment"
                                 @onCancelClick="onCancelClick">
        </student-assignment-form>
      </div>
    </li>
    <li class="flex flex-inline justify-between w-full">
      <div v-if="!showEditForm" class="flex-1">
        <vs-chip class="w-3/5" color="primary" transparent>
          <span>{{ $t('class-overview.assignment-remark') }}</span>
        </vs-chip>
      </div>
      <div v-if="!showEditForm" class="w-1/5">
        <vs-chip color="primary" transparent>
          {{ $t('class-overview.assignment-send-date') }}
        </vs-chip>
      </div>
      <div v-if="!showEditForm" class="w-1/5">
        <vs-chip class="float-right" color="primary" transparent>
          {{ $t('class-overview.assignment-teacher-receive') }}
        </vs-chip>
      </div>
    </li>
    <!--LIST DETAIL-->
    <li v-for="(studentAssignment, subIndex) in studentAssignments" :key="studentAssignment.id + '_' + subIndex"
        class="w-full px-3 pl-0" style="list-style: none;">
      <div v-if="!showEditForm" class="flex justify-between items-center w-full px-3 pl-0">
        <div class="flex-1 flex justify-start items-start">
          <vs-avatar :text="`${subIndex+1}.`" class="bg-primary w-5 h-5 text-sm mr-2 p-2"/>
          <span class="text-sm mt-1"> {{ studentAssignment.remark }} </span>
          <feather-icon v-if="studentAssignment.uploadPathUri"
                        class="ml-1 cursor-pointer" icon="DownloadIcon"
                        svgClasses="w-5 h-5 text-primary stroke-current"
                        @click="onDownloadClick(studentAssignment.uploadPathUri)"/>
        </div>
        <div class="flex w-2/5 justify-between">
          <span class="w-16 text-right text-sm"> {{ assignmentDueDateFormatter(studentAssignment.studentSendDate) }}</span>
          <span class="w-16 text-right text-sm">
            {{ assignmentDueDateFormatter(studentAssignment.teacherReceiveDate) }}
            <feather-icon v-if="!studentAssignment.teacherReceiveDate"
                          class="ml-1 cursor-pointer float-right"
                          icon="TrashIcon"
                          svgClasses="ml-2 w-5 h-5 text-primary stroke-current"
                          @click="onDeleteClick(studentAssignment)"/>
          </span>
        </div>
      </div>
    </li>
  </vx-card>
</template>

<script>
import store from '@/store';
import moment from "moment";
import {config} from '@/utils/upload'
import {showStateNotification} from '@/utils/notification'
import {confirmDelete} from '@/utils/dialogs'
import {createHelpers} from 'vuex-map-fields';
import {createNamespacedHelpers} from "vuex";
import StudentAssignmentForm from "@/views/pages/E-Learning/ClassOverview/Components/StudentAssignmentForm";
import StudentAssignment from '@/store/modules/student-assignment/index';

if (!store.state.StudentAssignment) store.state.registerModule(`StudentAssignment`, StudentAssignment);

const {
  mapActions: mapStudentAssignmentActions,
} = createNamespacedHelpers(`StudentAssignment`);

const {
  mapFields: mapStudentAssignmentFields,
} = createHelpers({
  getterType: `StudentAssignment/getField`,
  mutationType: `StudentAssignment/updateField`,
});

export default {
  name: "ClassAssignmentInfo",
  props:[
    'classAssignments',
    'studentEnrollDetailId',
    'classSection',
    'student',
  ],
  components: {
    StudentAssignmentForm
  },
  data() {
    return {
      showEditForm: false,
      studentAssignments: [],
      e_code: !localStorage.getItem("rsc") ? null : JSON.parse(localStorage.getItem("rsc")).e_code,
      r_stage: !localStorage.getItem("rsc") ? null : JSON.parse(localStorage.getItem("rsc")).r_stage,
    }
  },
  computed: {
    ...mapStudentAssignmentFields({
      studentAssignmentProcessing: 'processing',
      studentAssignmentSuccess: 'success',
      studentAssignmentError: 'error'
    }),
  },
  watch: {
    studentAssignmentSuccess(data) {
      showStateNotification(this.$vs, data);
    },
    studentAssignmentError: async function (data) {
      showStateNotification(this.$vs, data);
    },
  },
  methods:{
    ...mapStudentAssignmentActions([
      'getStudentAssignmentsByFilter',
      'addStudentAssignment',
      'deleteStudentAssignment',
      'uploadStudentAssignment',
    ]),
    onSendAssignmentClick(assignment) {
      assignment.showEditForm = !assignment.showEditForm;
      this.showEditForm = assignment.showEditForm;
    },
    onCancelClick(assignment) {
      assignment.showEditForm = !assignment.showEditForm;
      this.showEditForm = assignment.showEditForm;
    },
    onDownloadClick(fileUUID) {
      const url = `${config.url.downloadFile}${this.e_code}/${this.r_stage}/${fileUUID}`;
      // const url = config.url.downloadFile + fileUUID;
      window.open(url, '_blank');
    },
    async onDeleteClick(studentAssignment) {
      const payload = {
        id: studentAssignment.id
      };
      await confirmDelete(this.$vs, this.removeStudentAssignment, payload);

    },
    async saveStudentAssignment(payload) {
      let result = await this.addStudentAssignment(payload);
      this.showEditForm = false;
      if (result) {
        await this.loadData();
        return result;
      } else {
        return null;
      }
    },
    async removeStudentAssignment(payload) {
      const result = await this.deleteStudentAssignment(payload);
      if (result) {
        const index = this.studentAssignments.findIndex(item => item.id === result.id)
        this.studentAssignments =
          this.studentAssignments = [
            ...this.studentAssignments.slice(0, index),
            ...this.studentAssignments.slice(index + 1)
          ];
      }
    },
    assignmentDueDateFormatter(date) {
      if (date) {
        return moment(date).format('DD/MM/YYYY HH:mm:ss');
      }
    },
    async loadData() {
      let payload = {
        studentAssignmentFilterInput: {
          classSectionId: this.classSection.id,
          studentId: this.student.id,
        }
      };
      let result = await this.getStudentAssignmentsByFilter(payload);

      if (result) {
        this.studentAssignments = result
      }
    }
  },
  async beforeMount() {
    await this.loadData();
  }
}
</script>

<style scoped>

</style>
